/**
 * `localStorage`, wrapped so a blocked one is never a boot failure.
 *
 * Every persisted client preference goes through these two functions. There is
 * one rule and it is the `try`/`catch`: **storage can throw outright** in a
 * partitioned or privacy-restricted context — not return null, throw — and a
 * remembered theme is not worth taking the app down for. The same is true of
 * every other preference that has since been added beside it.
 *
 * This lives here rather than in `theme.ts` because it had already been
 * hand-copied three times (`theme.ts`, `TradeRoute`'s `readTimeframe`,
 * `wallet/chain-trust-store.ts`) and each copy is a place the `catch` can be
 * forgotten. New preferences import these; they do not write a fourth pair.
 *
 * Keys are `ud.`-prefixed, dot-separated and lowercase, with a version suffix
 * only where the stored shape may change (`ud.chain-trust.v1`).
 *
 * No React, no DOM beyond `globalThis.localStorage`.
 */

/**
 * A stored string, validated by its own guard.
 *
 * The guard runs on the **raw** value, so a key holding something this build
 * does not recognise — an older enum member, a hand-edited value — falls back
 * rather than being trusted through a cast.
 */
export function readStored<T>(key: string, guard: (v: unknown) => v is T, fallback: T): T {
  try {
    const raw = globalThis.localStorage?.getItem(key)
    return guard(raw) ? raw : fallback
  } catch {
    return fallback
  }
}

/** Write, or silently do nothing where storage refuses. */
export function writeStored(key: string, value: string): void {
  try {
    globalThis.localStorage?.setItem(key, value)
  } catch {
    /* see `readStored` */
  }
}

/**
 * A stored boolean, as the string `'1'` or `'0'`.
 *
 * Not `JSON.parse`: the values this client stores are single tokens, and a
 * parser here would accept `{"__proto__":…}` from a key anyone can edit in a
 * devtools console. `'1'` is the only truthy spelling.
 */
export function readStoredFlag(key: string, fallback: boolean): boolean {
  return readStored(key, isFlag, fallback ? '1' : '0') === '1'
}

/** Write a boolean in the spelling `readStoredFlag` reads. */
export function writeStoredFlag(key: string, value: boolean): void {
  writeStored(key, value ? '1' : '0')
}

const isFlag = (v: unknown): v is '1' | '0' => v === '1' || v === '0'
